/**
 * AnnouncementBarEffect creates a vertical 3D cube rotation transition between slides.
 *
 * Designed for announcement bars and text tickers, slides rotate around the X-axis
 * (up/down) instead of the Y-axis, creating a vertical cube-flip feel.
 *
 * The effect uses two main transition ranges (180° total, 90° at midpoint):
 * - L1 → CL1: Incoming slide rotates around its BOTTOM edge (180° → 0°)
 * - CL1 → R1: Outgoing slide rotates around its TOP edge (0° → -180°)
 */

// Factory function - class is created when TarotEffect is available
function createAnnouncementBarEffect(TarotEffect) {
	return class AnnouncementBarEffect extends TarotEffect {
		static effectName = 'announcement-bar';

		/**
		 * Effect configuration rules that define carousel behavior constraints
		 */
		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: 1,
			loopBuffer: { left: 1, right: 1 },
		};

		/**
		 * Creates a new announcement bar effect instance
		 * @param {Object} ctx - The shared context object
		 */
		constructor(ctx) {
			super(ctx);
			this.#slideHeight = 0;
			this.init();
		}

		/** @type {number} Cached slide height */
		#slideHeight;

		init() {
			super.init();
		}

		reInit() {
			super.reInit();
			this.#slideHeight = 0; // Reset cache on reinit
		}

		/**
		 * Get the height of the first slide, cached for performance
		 * @param {Array} slides - Array of slide elements
		 * @returns {number} Slide height in pixels
		 */
		getSlideHeight(slides) {
			if (!this.#slideHeight && slides.length > 0) {
				this.#slideHeight = slides[0].offsetHeight;
			}
			return this.#slideHeight;
		}

		/**
		 * Main render function called every animation frame to create vertical cube rotations
		 *
		 * @param {Object} frame - Complete frame object with all carousel data
		 * @param {Object} utils - Frame utilities for position calculations
		 */
		render(frame, utils) {
			const { slides, widths, animation } = frame;

			this.renderSlideWidth(widths.slide);

			const slideHeight = this.getSlideHeight(slides);

			for (let slideIndex = 0, totalSlides = slides.length; slideIndex < totalSlides; slideIndex++) {
				const slide = slides[slideIndex];

				// Check if slide is in the center visible position
				const centerRange = utils.isSlideInRange(slide, 'CR1', 'CL1');
				if (centerRange.isInRange) {
					this.applyCenterFace(slide, animation.isAnimating, widths);
					continue;
				}

				// Check if slide is in the incoming transition range (L1 → CL1)
				const incomingRange = utils.isSlideInRange(slide, 'CL1', 'L1');
				if (incomingRange.isInRange) {
					this.applyIncomingFace(slide, incomingRange.percent, animation.isAnimating, widths, slideHeight);
					continue;
				}

				// Check if slide is in the outgoing transition range (CR1 → R1)
				const outgoingRange = utils.isSlideInRange(slide, 'R1', 'CR1');
				if (outgoingRange.isInRange) {
					this.applyOutgoingFace(slide, outgoingRange.percent, animation.isAnimating, widths, slideHeight);
					continue;
				}

				// Outside visible ranges - hide the slide
				this.applyHiddenState(slide);
			}
		}

		/**
		 * Apply styling for center face (CL1 → CR1)
		 * The visible slide in the center with no rotation
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft
		 */
		applyCenterFace(slide, isAnimating, widths) {
			const translateX = widths.paddingLeft;

			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0)`
				: `translateX(${translateX}px)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = 'center center';
			slide.style.transformStyle = 'preserve-3d';
			slide.style.zIndex = '3';
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Apply vertical cube rotation for incoming slides (L1 → CL1)
		 * Slides rotate 180° total — at 50% of the transition they're at 90° (edge-on)
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress through transition (0 = at L1, 1 = at CL1)
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft
		 * @param {number} slideHeight - Height of a slide in pixels
		 */
		applyIncomingFace(slide, transitionPercent, isAnimating, widths, slideHeight) {
			// New slide rises from below: -180° → 0° (at 50% it's at -90°, edge-on)
			const maxRotation = 160;
			const rotateAmount = -maxRotation * (1 - transitionPercent); // -180° → 0°

			const translateX = widths.paddingLeft;

			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0) rotateX(${rotateAmount}deg)`
				: `translateX(${translateX}px) rotateX(${rotateAmount}deg)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = 'center top';
			slide.style.transformStyle = 'preserve-3d';
			slide.style.zIndex = '2';
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Apply vertical cube rotation for outgoing slides (CR1 → R1)
		 * Slides rotate 180° total — at 50% of the transition they're at -90° (edge-on)
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress through transition (0 = at CR1, 1 = at R1)
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft
		 * @param {number} slideHeight - Height of a slide in pixels
		 */
		applyOutgoingFace(slide, transitionPercent, isAnimating, widths, slideHeight) {
			// Current slide exits upward: 0° → 180° (at 50% it's at 90°, edge-on)
			const maxRotation = 160;
			const rotateAmount = maxRotation * transitionPercent; // 0° → 180°

			const translateX = widths.paddingLeft;

			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0) rotateX(${rotateAmount}deg)`
				: `translateX(${translateX}px) rotateX(${rotateAmount}deg)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = 'center bottom';
			slide.style.transformStyle = 'preserve-3d';
			slide.style.zIndex = '1';
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Hide slides that are outside the visible rotation ranges
		 *
		 * @param {HTMLElement} slide - The slide element to hide
		 */
		applyHiddenState(slide) {
			slide.style.opacity = '0';
			slide.hide();
			slide.style.zIndex = '0';
			slide.style.transform = 'translate3d(0, 0, 0)';
			slide.style.transformOrigin = 'center center';
			slide.style.transformStyle = '';
		}

		/**
		 * Clean up the effect when it's being destroyed or replaced
		 */
		destroy() {
			super.destroy();

			const slides = this.ctx.store.getSlides();
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform = '';
				slide.style.transformOrigin = '';
				slide.style.transformStyle = '';
				slide.style.opacity = '';
				slide.style.zIndex = '';
				slide.show?.();
			}
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'announcement-bar',
	factory: createAnnouncementBarEffect
});

export default createAnnouncementBarEffect;
